import { useContext, useEffect, useState } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	useCanGoBack,
	useNavigate,
	useParams,
	useRouter,
	useSearch,
} from '@tanstack/react-router';
import { gql, GraphQLClient } from 'graphql-request';

import { Article, USER_ROLE, clone } from '@/constants';
import { AppContext } from '@/App.context';
import { QueryRequest } from '@/services/query-client/QueryClient';
import {
	AUTH_KEY,
	getHTTPHeaders,
	GRAPHQL_URL,
} from '../home/auth/Authentication.hooks';
import config from '@/config';
import { buildStyles } from '@/components/article/StyleBuilder';

/**
 * React hook that fetches for an article
 *
 */
export function useGetArticle(): GetArticleResponse {
	const articleId = useParams({
		strict: false,
		select: (params) => params.id,
	});
	const router = useRouter();
	const queryClient = useQueryClient();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate();
	const [article, setArticle] = useState<Article | null | undefined>();
	const {
		queryRequest,
		onlineStatus: isOnline,
		baseUrl,
	} = useContext(AppContext);

	const queryKey = ['article', articleId];

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheTime = isAdmin
		? 0
		: 1000 * 60 * (config.app?.cache?.ttl?.getIssue || 60);

	const back = () => {
		if (canGoBack) {
			router.history.back();
			return;
		}
		navigate({
			to: baseUrl,
			replace: true,
		});
	};

	useArticleNotFound({ article, back });

	const { isPending, error, data } = useQuery<GetArticleQuery | null>({
		queryKey,
		networkMode: 'always',
		queryFn: async () => {
			return await getArticle({
				id: articleId,
				queryClient,
				queryKey,
				queryRequest,
				isOnline,
				back,
			});
		},
		staleTime: isOnline ? cacheTime : Infinity,
		gcTime: isOnline ? cacheTime : Infinity,
		retry: isOnline ? 3 : 0,
	});

	useEffect(() => {
		if (!data) return;

		const cloned = clone(data.article) as Article;
		const articleStyles = data.article?.styles;
		if (articleStyles) {
			cloned.styles = buildStyles(articleStyles);
		}

		setArticle(cloned);
	}, [data]);

	return {
		isLoading: isPending,
		article,
		error: error ? new Error(error.message) : null,
	};
}

export interface GetArticleResponse {
	isLoading: boolean;
	article?: Article | null;
	error: Error | null;
}

/**
 * React hook that handles in case an article is not found
 * or an error happens when loading
 *
 */
function useArticleNotFound({ article, back }: ArticleNotFoundParams) {
	useEffect(() => {
		if (article === null) {
			back();
			alert('Article not found');
			return;
		}
	}, [article]);
}

interface ArticleNotFoundParams {
	article: Article | null | undefined;
	back: () => void;
}

async function getArticle(params: GetArticleParams): Promise<GetArticleQuery> {
	const { id, queryClient, queryKey, isOnline, back, queryRequest } = params;
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cached: GetArticleQuery | undefined =
		queryClient.getQueryData(queryKey);
	// The app is offline
	if (!isOnline) {
		// If i don't have the data in the cache redirect to the homescreen
		if (!cached) {
			back();
			return { article: null };
		}

		// If i have the data return it
		return cached;
	}

	const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
	let headers = getHTTPHeaders(false);
	let client = new GraphQLClient(GRAPHQL_URL, { headers });

	// The user is not authenticated so i won't request access token
	if (!isAuthenticated) {
		try {
			return queryRequest.queryWithClient(
				client,
				getArticleQueryDocument,
				{
					id,
				},
			);
		} catch (e) {
			console.error(e);
			if (cached) return cached;
		}
	}

	if (isAdmin) {
		headers = getHTTPHeaders();
		client = new GraphQLClient(GRAPHQL_URL, { headers });
		return queryRequest.queryWithClient(client, getArticleQueryDocument, {
			id,
		});
	}

	return queryRequest.queryWithClient(client, getArticleQueryDocument, {
		id,
	});
}

interface GetArticleParams {
	id: string;
	queryClient: QueryClient;
	queryKey: Array<any>;
	queryRequest: QueryRequest;
	isOnline: boolean;
	back: () => void;
}

interface GetArticleQuery {
	article: Article | null;
}

/**
 * React hook that load data from navigation
 *
 */
export function useTitle(args: TitleArgs) {
	const { article, lang } = args;

	useEffect(() => {
		if (!article) {
			return;
		}
		if (!lang) {
			return;
		}

		const { name, description } = article;

		if (name[lang]) {
			document.title = name[lang];
		}

		if (description) {
			const metaHeader = document.querySelector(
				'meta[name="description"]',
			);
			if (metaHeader) {
				metaHeader.setAttribute('content', description);
			}
		}
	}, [article]);
}

interface TitleArgs {
	article: any | null | undefined;
	lang: string;
}

/**
 * React hook that control font
 *
 */
export function useFont(): FontResponse {
	const [fontSize, setFontSize] = useState(
		localStorage.getItem('font-size')
			? parseFloat(`${localStorage.getItem('font-size')}`)
			: 62.5,
	);
	useEffect(() => {
		localStorage.setItem('font-size', `${fontSize}`);
	}, [fontSize]);
	return {
		fontSize,
		setFontSize,
	};
}

interface FontResponse {
	fontSize: number;
	setFontSize: (size: number) => void;
}

export function useLanguages(article: Article | null | undefined): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!article) {
			return;
		}

		const acc = new Set<string>();
		for (const lang in article.name) {
			acc.add(lang);
		}
		setLanguages(acc);
	}, [article]);

	return languages;
}

export const getArticleQueryDocument = gql`
	query GetArticle($id: ID!) {
		article(id: $id) {
			id
			isFeatured
			features
			thumbnail
			components
			slug
			role
			style
			featureImage
			name
			slug
			features
			keywords
			channel
			styleProperties
			index
			description
			pages
			style
			updated
			releaseDate
			created
			replica {
				name
				page
				image
				hotspots {
					url
					area {
						x
						y
						width
						height
					}
				}
			}
			styles {
				id
				name
				description
				properties
				supportedDevices
				type
				parent
				tablet
				desktop
				created
				lastModified
			}
		}
	}
`;
